/**
 * Review Actions Panel
 * 
 * Inline panel for acting on REVIEW_REQUIRED leads.
 * Two actions:
 * 1. Fix & Retry - Correct leadType/state and immediately trigger routing
 * 2. Archive - Permanently remove from routing flow
 */

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { X, Loader2, AlertTriangle, RotateCcw, Archive, Zap } from 'lucide-react';
import { DEFAULT_LEAD_TYPE_MAPPINGS, type LeadTypeMapping } from '@/lib/leads/leadTypes';
import { useArchiveReviewLead } from '@/hooks/useLeadsDatabase';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { routeRetryLead } from '@/lib/routingOrchestrator';
import { useMockAgents } from '@/contexts/MockAgentsContext';
import { useAllocationWeights } from '@/contexts/AllocationWeightsContext';
import { usePricingRules } from '@/contexts/PricingRulesContext';
import { useSystemControl } from '@/contexts/SystemControlContext';
import { areDevOverridesActive } from '@/lib/dev/agentOverrides';
import type { LifecycleLead } from '@/lib/leadStateMachine';

// US States for dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

interface ReviewActionsPanelProps {
  leadId: string;
  currentLeadTypeId: string;
  currentState: string;
  onSuccess?: () => void;
}

export function ReviewActionsPanel({
  leadId,
  currentLeadTypeId,
  currentState,
  onSuccess,
}: ReviewActionsPanelProps) {
  // Form state
  const [selectedLeadTypeId, setSelectedLeadTypeId] = useState(
    // Try to find a valid match, otherwise use first type
    DEFAULT_LEAD_TYPE_MAPPINGS.find(m => m.id === currentLeadTypeId)?.id ||
    DEFAULT_LEAD_TYPE_MAPPINGS[0].id
  );
  const [selectedState, setSelectedState] = useState(
    US_STATES.includes(currentState.toUpperCase()) ? currentState.toUpperCase() : 'TX'
  );
  const [isFixing, setIsFixing] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  
  // Context for routing
  const { agents } = useMockAgents();
  const { actions: weightsActions } = useAllocationWeights();
  const { actions: pricingActions } = usePricingRules();
  const { actions: systemActions } = useSystemControl();
  
  // Mutations
  const archiveLeadMutation = useArchiveReviewLead();
  
  // Get selected lead type mapping
  const selectedLeadType = useMemo(() => {
    return DEFAULT_LEAD_TYPE_MAPPINGS.find(m => m.id === selectedLeadTypeId);
  }, [selectedLeadTypeId]);
  
  // Group lead types by vertical
  const leadTypesByVertical = useMemo(() => {
    const groups: Record<string, LeadTypeMapping[]> = {
      life_insurance: [],
      mortgage: [],
    };
    for (const mapping of DEFAULT_LEAD_TYPE_MAPPINGS) {
      groups[mapping.vertical].push(mapping);
    }
    return groups;
  }, []);
  
  const devOverridesActive = areDevOverridesActive();
  
  const handleFixAndRoute = async () => {
    if (!selectedLeadType) return;
    
    setIsFixing(true);
    try {
      const now = Date.now();
      
      // 1. Update lead in database with corrected data and status → NEW
      const { data: updatedLeadData, error: updateError } = await supabase
        .from('leads')
        .update({
          lead_type_id: selectedLeadType.id,
          lead_type_name: selectedLeadType.name,
          vertical: selectedLeadType.vertical,
          state: selectedState.toUpperCase(),
          status: 'NEW' as const,
          validation_errors: null,
          last_updated_at: now,
        })
        .eq('id', leadId)
        .eq('status', 'REVIEW_REQUIRED')
        .select()
        .single();
      
      if (updateError) throw updateError;
      
      // 2. Build the lead object for routing
      // Note: routingContext is intentionally omitted - routeRetryLead will capture fresh context
      const leadForRouting: LifecycleLead = {
        id: leadId,
        leadTypeId: selectedLeadType.id,
        leadTypeName: selectedLeadType.name,
        vertical: selectedLeadType.vertical,
        state: selectedState.toUpperCase(),
        estimatedValue: Number((updatedLeadData as any).estimated_value) || 150,
        status: 'NEW',
        retryCount: 0,
        maxRetries: 2,
        createdAt: (updatedLeadData as any).created_at || now,
        lastUpdatedAt: now,
      };
      
      // 3. Route the lead using routeRetryLead for clean-slate evaluation
      // This ensures:
      // 1. Fresh DEV override evaluation from current session state
      // 2. Full normalization pipeline runs from scratch
      // 3. No stale eligibility context from prior failures
      const weights = weightsActions.getWeights();
      const result = routeRetryLead(leadForRouting, agents, weights, {
        getPricingRule: (leadTypeId, state) => {
          const rule = pricingActions.getPricingRuleForLead(leadTypeId, state);
          return rule ? { basePrice: rule.basePrice, active: rule.active } : null;
        },
        checkIntake: (vertical) => systemActions.getIntakeRejectionReason(vertical),
        targetPoolSize: 8,
        debug: true,
      });
      
      // CRITICAL: Await persistence before showing results
      if (result.persistence) {
        try {
          await result.persistence;
        } catch (persistErr) {
          console.error('[ReviewActions] Persistence failed:', persistErr);
          // Continue showing result even if persistence failed
        }
      }
      
      // 5. Show result
      if (result.success) {
        toast.success('Lead approved and routed', {
          description: `Routed to ${result.deliveryImpact.agentName} for $${result.deliveryImpact.chargedAmount}`,
        });
      } else if (result.outcome === 'RETRY_QUEUED') {
        toast.info('Lead approved - queued for retry', {
          description: 'No eligible agents found. Lead will retry automatically.',
        });
      } else {
        toast.warning('Lead approved but not routed', {
          description: result.observability.rejectionReason || 'Lead moved to stock.',
        });
      }
      
      onSuccess?.();
    } catch (error) {
      console.error('[ReviewActions] Fix & Route failed:', error);
      toast.error('Failed to process lead', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsFixing(false);
    }
  };
  
  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      await archiveLeadMutation.mutateAsync(leadId);
      
      toast.success('Lead archived', {
        description: 'Lead has been removed from the routing flow.',
      });
      
      onSuccess?.();
    } catch (error) {
      console.error('[ReviewActions] Archive failed:', error);
      toast.error('Failed to archive lead', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsArchiving(false);
    }
  };
  
  const isProcessing = isFixing || isArchiving;
  
  return (
    <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30 space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
          Review Actions
        </span>
        {devOverridesActive && (
          <Badge variant="outline" className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
            DEV MODE
          </Badge>
        )}
      </div>
      
      {/* Fix & Route Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <RotateCcw className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Fix & Route</span>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {/* Lead Type Dropdown */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Lead Type</Label>
            <Select
              value={selectedLeadTypeId}
              onValueChange={setSelectedLeadTypeId}
              disabled={isProcessing}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium">
                  Life Insurance
                </div>
                {leadTypesByVertical.life_insurance.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
                <Separator className="my-1" />
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium">
                  Mortgage
                </div>
                {leadTypesByVertical.mortgage.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* State Dropdown */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">State</Label>
            <Select
              value={selectedState}
              onValueChange={setSelectedState}
              disabled={isProcessing}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {US_STATES.map(state => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Button
          onClick={handleFixAndRoute}
          disabled={isProcessing || !selectedLeadType || agents.length === 0}
          size="sm"
          className="w-full"
        >
          {isFixing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Routing...
            </>
          ) : (
            <>
              <Zap className="h-4 w-4 mr-2" />
              Fix & Route Now
            </>
          )}
        </Button>
        
        {agents.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No agents available. Add agents to enable routing.
          </p>
        )}
      </div>
      
      <Separator />
      
      {/* Archive Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Archive className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Archive</span>
        </div>
        
        <p className="text-xs text-muted-foreground">
          Permanently remove this lead from the routing flow. It will remain visible for audit purposes.
        </p>
        
        <Button
          onClick={handleArchive}
          disabled={isProcessing}
          variant="outline"
          size="sm"
          className="w-full text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/50"
        >
          {isArchiving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Archiving...
            </>
          ) : (
            <>
              <X className="h-4 w-4 mr-2" />
              Archive Lead
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
